import classNames from "classnames";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { Tag } from "@design-system/Tag/Tag";
import {
    anatomyBindingValue,
    anatomyTokenTabId,
    type AnatomyBinding,
    type AnatomyPart,
    type AnatomyState,
    type ComponentAnatomySpec,
    type ResolvedAnatomyBinding,
} from "./componentAnatomy";
import { tokenCopyButtonId } from "./designTokensCatalog";
import { designSystemEntryPath } from "./playgroundComponents";
import type { AnatomyPartHighlight } from "./componentAnatomyParts";
import styles from "./ComponentAnatomyLegend.module.scss";

/**
 * @purpose The reading half of the anatomy page: every resolved binding for the
 * selected state, grouped by the visual part it paints, with each token name
 * deep-linking to its row in the design tokens playground.
 */

const isColorProperty = (property: string) =>
    property === "background" || property.replace(/\)$/, "").endsWith("color");

const TokenBindingLink = ({ token }: { token: `--${string}` }) => (
    <StateAwareLink
        aria-label={`Show token ${token} in the design tokens playground`}
        className={styles.tokenLink}
        to={`${designSystemEntryPath(anatomyTokenTabId(token))}#${tokenCopyButtonId(token)}`}
    >
        <code className={styles.tokenName}>{token}</code>
    </StateAwareLink>
);

const BindingRow = ({
    binding,
    setByState,
}: {
    binding: AnatomyBinding;
    setByState?: AnatomyState;
}) => (
    <li className={styles.bindingRow}>
        <span className={styles.bindingHead}>
            {isColorProperty(binding.property) && (
                <span
                    aria-hidden="true"
                    className={styles.swatch}
                    style={{ background: anatomyBindingValue(binding) }}
                />
            )}
            <code className={styles.property}>{binding.property}</code>
            <span aria-hidden="true" className={styles.arrow}>
                →
            </span>
            {binding.token === undefined ? (
                <code className={styles.literal}>{binding.literal}</code>
            ) : (
                <TokenBindingLink token={binding.token} />
            )}
            {binding.via !== undefined && (
                <code className={styles.via}>via {binding.via}</code>
            )}
            {setByState !== undefined && (
                <Tag size="sm" color="neutral">
                    {`set by ${setByState.label.toLowerCase()}`}
                </Tag>
            )}
        </span>
        {binding.note !== undefined && (
            <p className={styles.note}>{binding.note}</p>
        )}
    </li>
);

const PartGroup = ({
    part,
    partBindings,
    states,
    highlight,
}: {
    part: AnatomyPart;
    partBindings: readonly ResolvedAnatomyBinding[];
    states: readonly AnatomyState[];
    highlight: AnatomyPartHighlight;
}) => (
    <section
        className={classNames(styles.partGroup, {
            [styles.partGroupActive]: highlight.activePartId === part.id,
            [styles.partGroupMuted]:
                highlight.activePartId !== null &&
                highlight.activePartId !== part.id,
        })}
        onMouseEnter={() => highlight.onPartActivate(part.id)}
        onMouseLeave={() => highlight.onPartActivate(null)}
    >
        <h4 className={styles.partName}>{part.label}</h4>
        <code className={styles.partElement}>{part.element}</code>
        <ul className={styles.bindingList}>
            {partBindings.map(({ binding, setByStateId }) => (
                <BindingRow
                    key={binding.property}
                    binding={binding}
                    setByState={states.find(
                        (state) => state.id === setByStateId,
                    )}
                />
            ))}
        </ul>
    </section>
);

export const ComponentAnatomyLegend = ({
    spec,
    bindings,
    highlight,
}: {
    spec: ComponentAnatomySpec;
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
}) => (
    <div className={styles.legend}>
        {spec.parts.map((part) => {
            const partBindings = bindings.filter(
                (resolved) => resolved.binding.partId === part.id,
            );

            if (partBindings.length === 0) {
                return null;
            }

            return (
                <PartGroup
                    key={part.id}
                    part={part}
                    partBindings={partBindings}
                    states={spec.states}
                    highlight={highlight}
                />
            );
        })}
    </div>
);
